import { useEffect } from 'react';
import { useGetUserProfile } from '@leagueplatform/web-common';

import { getConfig } from '@leagueplatform/config';
import {
  onSalesforceEvent,
  SALESFORCE_EVENTS,
  setSalesforcePrechatFields,
} from '../embedded-chat';
import { buildHiddenPrechatFieldsFromProfile } from '../utils/build-hidden-prechat-fields-from-profile';

/**
 * React hook to populate Salesforce pre-chat fields with League-managed context.
 *
 * This hook:
 * - Fetches the current user's profile from League's user-profile API
 * - Waits for Salesforce Messaging to be ready
 * - Sends hidden pre-chat fields
 *
 * Hidden values are merged on top of any configuration-based hidden pre-chat
 * fields. League-managed values win on collisions.
 *
 * Usage:
 * - Call alongside `useInitSalesforceMessaging` and `useSalesforceUserVerification`
 *   in a top-level component that mounts for authenticated users.
 *
 */
export const useSalesforcePrechat = (enabled = true): void => {
  // Leverage existing React Query–backed hook; when `enabled` is false the
  // query will not run.
  const { data: profile } = useGetUserProfile(
    {
      enabled,
    },
    undefined,
  );
  useEffect(() => {
    if (!enabled) {
      // No-op cleanup when pre-chat is disabled, to satisfy hooks/ESLint expectations
      return () => {};
    }

    let isMounted = true;
    let messagingReadyForHook = false;

    const tryApplyPrechat = () => {
      if (!isMounted || !messagingReadyForHook || !profile) {
        return;
      }

      const { config } = getConfig(false);
      const { chatType } = config?.salesforceLiveChat ?? {};

      const browserLanguage =
        typeof navigator !== 'undefined' && navigator.language;

      const hiddenFields: Record<string, unknown> = {
        // Profile locale is mapped to `language` inside buildHiddenPrechatFieldsFromProfile
        // and acts as a fallback when browserLanguage is unavailable.
        ...buildHiddenPrechatFieldsFromProfile(profile),
        platform: 'web',
        ...(browserLanguage ? { language: browserLanguage } : {}),
        ...(chatType ? { chattype: chatType } : {}),
      };

      setSalesforcePrechatFields({ hidden: hiddenFields, replaceHidden: true });
    };

    const unsubscribeReadyListener = onSalesforceEvent(
      SALESFORCE_EVENTS.READY,
      () => {
        messagingReadyForHook = true;
        tryApplyPrechat();
      },
    );

    // Handle the case where Messaging was initialized earlier and READY fired
    // before this hook mounted.
    if (
      typeof window !== 'undefined' &&
      window.leagueSalesforceMessagingReady
    ) {
      messagingReadyForHook = true;
    }

    // Attempt immediately in case both Messaging and profile are already ready.
    tryApplyPrechat();

    return () => {
      isMounted = false;
      unsubscribeReadyListener();
    };
  }, [enabled, profile]);
};
